/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Injectable } from '@angular/core';
import { clusterCoordinator, clusterTopologyLoaded } from '@app/core/ngrx/app.selectors';
import { ResourceService } from '@app/core/services/resource.service';
import { WebSocketConnection } from '@app/core/services/websocket-connection.service';
import { DispatchSqlArgs } from '@app/sql/domain/query';
import { State } from '@app/sql/types';
import { downloadResult } from '@common/utils/download-result';
import { Store } from '@ngrx/store';
import { ActionName } from '@shared/types/cluster-actions';
import {
  ClusterActionScanExecuteArgument,
  ClusterActionSqlExecuteArgument,
  QueryExportMessage,
} from '@shared/types/queries';
import { filter, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ExportQueryService {
  constructor(
    private ws: WebSocketConnection,
    private store: Store<State>,
    private resourceService: ResourceService,
  ) {}

  export(action: ActionName.SQL_QUERY_EXECUTE, args: DispatchSqlArgs<ClusterActionSqlExecuteArgument>): void;
  export(action: ActionName.SCAN_QUERY_EXECUTE, args: DispatchSqlArgs<ClusterActionScanExecuteArgument>): void;
  export(
    action: ActionName.SQL_QUERY_EXECUTE | ActionName.SCAN_QUERY_EXECUTE,
    args: DispatchSqlArgs<ClusterActionSqlExecuteArgument | ClusterActionScanExecuteArgument>,
  ): void {
    this.store
      .select(clusterTopologyLoaded(args.clusterId))
      .pipe(
        filter(Boolean),
        switchMap(() => this.store.select(clusterCoordinator(args.clusterId))),
        take(1),
      )
      .subscribe((coordinator) =>
        this.ws.send(`/app/queries/exports/clusters/${args.clusterId}`, {
          id: args.argument.queryId,
          action,
          nodeIds: 'nodeIds' in args ? args.nodeIds : [coordinator?.nodeId],
          argument: 'argument' in args ? args.argument : null,
        }),
      );
  }

  getExportStatuses(clusterId: string) {
    return this.ws.getTopic<QueryExportMessage[] | QueryExportMessage>(
      `/user/topic/cluster/${clusterId}/queries/exports/status`,
    );
  }

  downloadResult(resultId: string) {
    return this.resourceService
      .getResource(resultId)
      .pipe(downloadResult('query-export-result.zip', 'application/octet-stream'));
  }
}
